import { getSortableStatementPeriodLabel } from './getSortableStatementPeriodLabel';

/**
 * @typedef {GetStatementRangeLabelInput}
 * @property {string} defaultPeriodLabel a string to use if either the first or last label can't be
 * determined
 * @property {StatementPeriod} firstStatementPeriod the first statement period
 * @property {StatementPeriod} lastStatementPeriod optional last statement period. If none is
 * provided, only the first period is used to generate the label
 * @property {string} pro name of the PRO
 * @property {Maybe<string>} region the region, i.e. 'standard' or 'international'
 */
interface GetStatementRangeLabelInput {
    defaultPeriodLabel: string;
    firstStatementPeriod: StatementPeriod;
    lastStatementPeriod?: StatementPeriod;
    pro: string;
    region: string | null;
    showPro?: boolean;
}

/**
 * Given either one or two statement periods, return a label that can be used for the statement
 * range.
 * @param {GetStatementRangeLabelInput} input
 * @returns {string}
 */
export const getStatementRangeLabel = (
    input: GetStatementRangeLabelInput,
): string => {
    const {
        defaultPeriodLabel,
        lastStatementPeriod,
        firstStatementPeriod,
        region,
        showPro,
    } = input;
    let { pro } = input;

    if (pro === 'ASCAP' && region === 'international') {
        pro = 'ASCAP Intl.';
    }

    const firstStatementPeriodLabel = getSortableStatementPeriodLabel(
        firstStatementPeriod,
        defaultPeriodLabel,
    );

    if (lastStatementPeriod === undefined) {
        if (showPro) {
            return `${pro} ${firstStatementPeriodLabel}`;
        }

        return firstStatementPeriodLabel;
    }

    const lastStatementPeriodLabel = getSortableStatementPeriodLabel(
        lastStatementPeriod,
        defaultPeriodLabel,
    );

    if (showPro) {
        return `${pro} ${firstStatementPeriodLabel} - ${lastStatementPeriodLabel}`;
    }

    return `${firstStatementPeriodLabel} - ${lastStatementPeriodLabel}`;
};
